import * as React from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import { GeoComponent, TooltipComponent } from "echarts/components";
import { ScatterChart, LinesChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";
import { Button } from "antd";
import { PlusOutlined, MinusOutlined, AimOutlined } from "@ant-design/icons";
import { useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { request } from "../../../services/cpln";
import { GeoRouteEntry } from "../../../reactHooks/useGeoRoute";
import { Location } from "../../../schema/types/location/entity";
import { loadWorldGeoJson } from "../../workload/detail/WorldMapPanel/worldGeoJson";

echarts.use([GeoComponent, TooltipComponent, ScatterChart, LinesChart, CanvasRenderer]);

interface GeoData {
  lat: number;
  lon: number;
  city?: string;
  country?: string;
}

const reasonColors: Record<string, string> = {
  geo: "#30bd80",
  httpLatency: "#30bd80",
  fallback: "#f5a623",
  health: "#ff4444",
  default: "#0084ff",
};

const mapTheme = {
  light: {
    land: "#f0f7f2",
    border: "#c8d9cb",
    text: "#333",
    ocean: "#e8f4fc",
  },
  dark: {
    land: "#1e2a22",
    border: "#2d3d32",
    text: "#fff",
    ocean: "#141c20",
  },
};

const TARGET_COLOR = "#30bd80"; // green
const EDGE_COLOR = "#0084ff"; // blue

interface Props {
  data: GeoRouteEntry[];
}

export const GeoRouteMap: React.FC<Props> = ({ data }) => {
  const theme = useAppSelector((state) => state.theme.theme);
  const org = useAppSelector(selectOrg);
  const [mapRegistered, setMapRegistered] = React.useState(false);
  const [geoCache, setGeoCache] = React.useState<Record<string, GeoData>>({});
  const chartRef = React.useRef<ReactECharts>(null);
  const [isInitialZoomApplied, setIsInitialZoomApplied] = React.useState(false);
  const [initialView, setInitialView] = React.useState<{ zoom: number; center: [number, number] } | null>(null);

  // Load world map
  React.useEffect(() => {
    let cancelled = false;
    async function registerMap() {
      try {
        if (echarts.getMap("world")) {
          setMapRegistered(true);
          return;
        }
        const geoJson = await loadWorldGeoJson();
        if (cancelled) return;
        echarts.registerMap("world", geoJson);
        setMapRegistered(true);
      } catch (err) {
        console.error("Failed to load map:", err);
      }
    }
    registerMap();
    return () => {
      cancelled = true;
    };
  }, []);

  // Collect ALL location names: both edge locations and targets
  const edgeLocationSet = React.useMemo(() => new Set(data.map((d) => d.location)), [data]);
  const allLocationNames = React.useMemo(
    () => Array.from(new Set([...data.map((d) => d.location), ...data.map((d) => d.target)])),
    [data],
  );
  const targetSet = React.useMemo(() => new Set(data.map((d) => d.target)), [data]);
  const targetOnlyNames = React.useMemo(
    () => allLocationNames.filter((name) => !edgeLocationSet.has(name)),
    [allLocationNames, edgeLocationSet],
  );

  // Fetch geo data for all locations (edges + targets)
  React.useEffect(() => {
    const missing = allLocationNames.filter((name) => !geoCache[name]);
    if (missing.length === 0) return;

    let cancelled = false;

    async function fetchGeoData() {
      const newCache: Record<string, GeoData> = {};
      await Promise.all(
        missing.map(async (locationName) => {
          try {
            const { data: location } = await request<Location>({
              url: `/org/${org}/location/${locationName}`,
            });
            const geo = location.status?.geo;
            if (geo?.lat !== undefined && geo?.lon !== undefined) {
              newCache[locationName] = {
                lat: geo.lat,
                lon: geo.lon,
                city: geo.city,
                country: geo.country,
              };
            }
          } catch {
            // Skip locations we can't resolve
          }
        }),
      );
      if (!cancelled) {
        setGeoCache((prev) => ({ ...prev, ...newCache }));
      }
    }

    fetchGeoData();
    return () => {
      cancelled = true;
    };
  }, [allLocationNames.join(","), org]);

  const colors = theme === "dark" ? mapTheme.dark : mapTheme.light;

  // Group entries by location for tooltip
  const entriesByLocation = React.useMemo(() => {
    const map = new Map<string, GeoRouteEntry[]>();
    for (const entry of data) {
      const existing = map.get(entry.location) || [];
      existing.push(entry);
      map.set(entry.location, existing);
    }
    return map;
  }, [data]);

  // Compute zoom/center using ALL locations
  const computedView = React.useMemo(() => {
    const markers = allLocationNames.map((n) => geoCache[n]).filter(Boolean);
    if (markers.length === 0) return { zoom: 1.2, center: [0, 20] as [number, number] };

    const lons = markers.map((m) => m.lon);
    const lats = markers.map((m) => m.lat);
    const center: [number, number] = [(Math.min(...lons) + Math.max(...lons)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
    const maxSpan = Math.max(Math.max(...lons) - Math.min(...lons), Math.max(...lats) - Math.min(...lats), 10);
    const zoom = Math.min(8, Math.max(1.5, 180 / (maxSpan * 1.5)));
    return { zoom, center };
  }, [allLocationNames, geoCache]);

  React.useEffect(() => {
    const hasMarkers = allLocationNames.some((n) => geoCache[n]);
    if (hasMarkers && !isInitialZoomApplied) {
      setIsInitialZoomApplied(true);
      setInitialView(computedView);
    }
  }, [allLocationNames, geoCache, isInitialZoomApplied, computedView]);

  const option: EChartsOption = React.useMemo(() => {
    if (!mapRegistered) return {};

    // Edge location markers
    const edgeMarkers = Array.from(edgeLocationSet)
      .filter((name) => geoCache[name])
      .map((name) => {
        const geo = geoCache[name];
        const entries = entriesByLocation.get(name) || [];
        const isAlsoTarget = targetSet.has(name);
        return {
          name,
          value: [geo.lon, geo.lat],
          entries,
          geo,
          markerType: isAlsoTarget ? "target" : "edge",
          itemStyle: { color: isAlsoTarget ? TARGET_COLOR : EDGE_COLOR },
        };
      });

    // Target-only markers (not already an edge location)
    const targetMarkers = targetOnlyNames
      .filter((name) => geoCache[name])
      .map((name) => {
        const geo = geoCache[name];
        return {
          name,
          value: [geo.lon, geo.lat],
          entries: [] as GeoRouteEntry[],
          geo,
          markerType: "target",
          itemStyle: { color: TARGET_COLOR },
        };
      });

    // Route lines from edge → target
    const routeLines = data
      .filter((entry) => geoCache[entry.location] && geoCache[entry.target])
      .map((entry) => {
        const from = geoCache[entry.location];
        const to = geoCache[entry.target];
        const color = reasonColors[entry.reason] || reasonColors.default;
        return {
          coords: [
            [from.lon, from.lat],
            [to.lon, to.lat],
          ],
          entry,
        };
      });

    const geoConfig: any = {
      map: "world",
      roam: true,
      scaleLimit: { min: 1, max: 10 },
      itemStyle: { areaColor: colors.land, borderColor: colors.border, borderWidth: 0.5 },
      emphasis: { disabled: true },
      silent: true,
    };

    if (!isInitialZoomApplied) {
      geoConfig.zoom = computedView.zoom;
      geoConfig.center = computedView.center;
    }

    const series: any[] = [];

    // Lines series (behind markers)
    series.push({
      type: "lines",
      coordinateSystem: "geo",
      data: routeLines,
      lineStyle: {
        color: EDGE_COLOR,
        width: 1.5,
        curveness: 0.2,
        opacity: 0.4,
      },
      emphasis: {
        lineStyle: {
          width: 3,
          opacity: 1,
        },
      },
      zlevel: 0,
    });

    // Target-only markers (diamonds)
    if (targetMarkers.length > 0) {
      series.push({
        type: "scatter",
        coordinateSystem: "geo",
        data: targetMarkers,
        symbol: "diamond",
        symbolSize: 12,
        label: {
          show: true,
          formatter: "{b}",
          position: "bottom",
          fontSize: 11,
          color: colors.text,
          distance: 5,
        },
        labelLayout: { hideOverlap: true },
        zlevel: 1,
      });
    }

    // Edge location markers (circles)
    series.push({
      type: "scatter",
      coordinateSystem: "geo",
      data: edgeMarkers,
      symbolSize: 14,
      label: {
        show: true,
        formatter: "{b}",
        position: "bottom",
        fontSize: 11,
        color: colors.text,
        distance: 5,
      },
      labelLayout: { hideOverlap: true },
      zlevel: 2,
    });

    return {
      backgroundColor: colors.ocean,
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          if (!params.data) return "";

          // Line tooltip
          if (params.seriesType === "lines") {
            const entry: GeoRouteEntry = params.data.entry;
            if (!entry) return "";
            const color = reasonColors[entry.reason] || reasonColors.default;
            const latencyStr = entry.latencyMs !== null ? (entry.latencyMs % 1 === 0 ? String(entry.latencyMs) : entry.latencyMs.toFixed(1)) : null;
            let html = `<div style="font-family: var(--font-family-primary);">`;
            html += `<div style="font-weight: 600; margin-bottom: 4px;">${entry.location} → ${entry.target}</div>`;
            html += `<div style="display: flex; align-items: center; gap: 6px;">`;
            html += `<span style="width: 8px; height: 8px; border-radius: 50%; background: ${color}; flex-shrink: 0;"></span>`;
            html += `<span style="font-size: 12px;">${entry.reason}${latencyStr !== null ? ` · ${latencyStr} ms` : ""}</span>`;
            html += `</div>`;
            html += `</div>`;
            return html;
          }

          // Marker tooltip
          const { name, entries, geo, markerType } = params.data;
          const label = markerType === "target" ? "Target" : "Edge";
          let html = `<div style="font-family: var(--font-family-primary);">`;
          html += `<div style="font-weight: 600; margin-bottom: 4px;">${name}</div>`;
          if (geo.city || geo.country) {
            html += `<div style="color: #888; font-size: 12px; margin-bottom: 2px;">${[geo.city, geo.country].filter(Boolean).join(", ")}</div>`;
          }
          html += `<div style="color: #888; font-size: 11px; margin-bottom: 6px;">${label}</div>`;
          for (const entry of entries) {
            const color = reasonColors[entry.reason] || reasonColors.default;
            const latencyStr = entry.latencyMs !== null ? (entry.latencyMs % 1 === 0 ? String(entry.latencyMs) : entry.latencyMs.toFixed(1)) : null;
            html += `<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px;">`;
            html += `<span style="width: 8px; height: 8px; border-radius: 50%; background: ${color}; flex-shrink: 0;"></span>`;
            html += `<span style="font-size: 12px;"><b>${entry.reason}</b> → ${entry.target}${latencyStr !== null ? ` · ${latencyStr} ms` : ""}</span>`;
            html += `</div>`;
          }
          html += `</div>`;
          return html;
        },
      },
      geo: geoConfig,
      series,
    };
  }, [mapRegistered, allLocationNames, geoCache, edgeLocationSet, targetOnlyNames, entriesByLocation, data, colors, isInitialZoomApplied, computedView]);

  const handleZoomIn = React.useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (instance) {
      const opt = instance.getOption() as any;
      const currentZoom = opt.geo?.[0]?.zoom ?? 1.2;
      instance.setOption({ geo: { zoom: Math.min(10, currentZoom * 1.5) } });
    }
  }, []);

  const handleZoomOut = React.useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (instance) {
      const opt = instance.getOption() as any;
      const currentZoom = opt.geo?.[0]?.zoom ?? 1.2;
      instance.setOption({ geo: { zoom: Math.max(1, currentZoom / 1.5) } });
    }
  }, []);

  const handleReset = React.useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (instance && initialView) {
      instance.setOption({ geo: { zoom: initialView.zoom, center: initialView.center } });
    }
  }, [initialView]);

  const onEvents = React.useMemo(
    () => ({
      georoam: () => {
        const instance = chartRef.current?.getEchartsInstance();
        if (instance) {
          instance.setOption({}, { lazyUpdate: false });
        }
      },
    }),
    [],
  );

  if (!mapRegistered) return null;

  return (
    <div style={{ position: "relative" }}>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: 350, width: "100%", borderRadius: 6, overflow: "hidden" }}
        opts={{ renderer: "canvas" }}
        notMerge={false}
        onEvents={onEvents}
      />
      <div style={{ position: "absolute", bottom: 12, left: 12, display: "flex", flexDirection: "column", gap: 4 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={handleZoomIn} />
        <Button size="small" icon={<MinusOutlined />} onClick={handleZoomOut} />
        <Button size="small" icon={<AimOutlined />} onClick={handleReset} />
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 12,
          right: 12,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "8px 12px",
          borderRadius: 6,
          backgroundColor: theme === "dark" ? "rgba(20, 28, 32, 0.85)" : "rgba(255, 255, 255, 0.85)",
          fontSize: 12,
          color: colors.text,
          pointerEvents: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: TARGET_COLOR, flexShrink: 0 }} />
          <span>Server Location</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: EDGE_COLOR, flexShrink: 0 }} />
          <span>Edge Location</span>
        </div>
      </div>
    </div>
  );
};
